import React, { useCallback } from 'react';
import {
  ActivityType,
  Anchor,
  Box,
  BoxExtendedProps,
  CategoryIcon,
  Checkbox,
  costFormater,
  List,
  Text,
} from '@common-fe/common-fe';
import dayjs from 'dayjs';

import { EMPTY_FIELD_VALUE, SHORT_DATE_FORMAT } from '@/common/constants';
import ROUTES from '@/common/routes';

interface Props extends Pick<BoxExtendedProps, 'background' | 'border'> {
  claimId?: string;
  providerName?: string;
  categoryType?: string;
  serviceName?: string;
  amount?: number;
  serviceOn?: string;
  isChecked?: boolean;
  onCheck?: (value: string) => void;
  isMobile?: boolean;
  employeeId: string;
}

const AssociateWithClaimsListItem: React.FC<Props> = ({
  claimId,
  providerName,
  categoryType,
  serviceName,
  amount,
  serviceOn,
  isChecked,
  onCheck,
  isMobile,
  employeeId,
  ...props
}) => {
  const handleCheck = useCallback(() => {
    if (claimId) {
      onCheck?.(claimId);
    }
  }, [claimId, onCheck]);

  if (isMobile) {
    return (
      <List.Item
        testId="associate-with-claims-list_item"
        pad="spacing12"
        gap="spacing8"
        disableHover
        {...props}
      >
        <Box direction="row" align="center" gap="spacing8" flex>
          {onCheck ? (
            <Checkbox
              testId="associate-with-claims-list_select-item"
              checked={isChecked}
              onChange={handleCheck}
            />
          ) : null}
          <CategoryIcon category={categoryType as ActivityType} />
          <Box direction="column" flex>
            <Anchor href={ROUTES.TRANSACTION_DETAILS_CLAIM(employeeId, claimId)} target="_blank">
              <Text title={providerName} weight={400} className="ellipsis-text">
                {providerName || EMPTY_FIELD_VALUE}
              </Text>
            </Anchor>
            <List.Item.Text title={serviceName} weight={400} truncate>
              {serviceName || EMPTY_FIELD_VALUE}
            </List.Item.Text>
          </Box>
        </Box>

        <Box direction="column">
          <List.Item.Text title={serviceName} weight={700} textAlign="end" testId={`associate-with-claims-list_item_${claimId}_amount-mobile`}>
            {typeof amount === 'number' ? costFormater(amount, true) : EMPTY_FIELD_VALUE}
          </List.Item.Text>
          <List.Item.Text title={serviceName} weight={400} textAlign="end" color="textSecondary" testId={`associate-with-claims-list_item_${claimId}_date-mobile`}>
            {serviceOn ? dayjs(serviceOn).format(SHORT_DATE_FORMAT) : EMPTY_FIELD_VALUE}
          </List.Item.Text>
        </Box>
      </List.Item>
    );
  }

  return (
    <List.Item
      testId="associate-with-claims-list_item"
      pad={{ horizontal: 'spacing24', vertical: 'spacing12' }}
      disableHover
      {...props}
    >
      {onCheck ? (
        <List.Item.Text flex={0.5}>
          <Checkbox
            testId="associate-with-claims-list_select-item"
            checked={isChecked}
            onChange={handleCheck}
          />
        </List.Item.Text>
      ) : null}

      <List.Item.Text flex={2} testId={`associate-with-claims-list_item_${claimId}_provider`}>
        <Box direction="row" align="center" gap="spacing12">
          <CategoryIcon category={categoryType as ActivityType} />
          <Box flex>
            <Anchor href={ROUTES.TRANSACTION_DETAILS_CLAIM(employeeId, claimId)} target="_blank">
              <Text title={providerName} weight={400} className="ellipsis-text">
                {providerName || EMPTY_FIELD_VALUE}
              </Text>
            </Anchor>
          </Box>
        </Box>
      </List.Item.Text>

      <List.Item.Text 
        flex={1.5}
        weight={400}
        title={serviceName}
        truncate
        testId={`associate-with-claims-list_item_${claimId}_service`}
      >
        {serviceName || EMPTY_FIELD_VALUE}
      </List.Item.Text>

      <List.Item.Text
        flex={1}
        textAlign="center"
        testId={`associate-with-claims-list_item_${claimId}_amount`}
        weight={700}
      >
        {typeof amount === 'number' ? costFormater(amount, true) : EMPTY_FIELD_VALUE}
      </List.Item.Text>

      <List.Item.Text
        flex={0.7}
        weight={400}
        color="textSecondary"
        testId={`associate-with-claims-list_item_${claimId}_date`}
      >
        {serviceOn ? dayjs(serviceOn).format(SHORT_DATE_FORMAT) : EMPTY_FIELD_VALUE}
      </List.Item.Text>
    </List.Item>
  );
};

export default AssociateWithClaimsListItem;
